import type { ProgressResult, UserProgress } from '../components/Preview/api';
import {
  computeProfileCompletionScore,
  profileCompletionMessage,
  PROFILE_COMPLETION_SECTION_LABELS,
  PROFILE_COMPLETION_WEIGHTS,
  resolveScreeningReadiness,
  TARGET_SCREENING_PROMPT_COUNT,
  screeningPromptProgress,
  countScreeningPrompts,
  type ProfileCompletionBreakdownItem,
  type ProfileCompletionSection,
} from './profileCompletionScore';

/** @deprecated Legacy keys — kept for API compatibility with fetchUserProgress. */
const LEGACY_PROGRESS_WEIGHTS: Record<keyof UserProgress, number> = {
  login: 5,
  welcome: 15,
  details: 20,
  firstPrompt: 25,
  allPrompts: 30,
  profilePic: 5,
};

/** User-facing labels for legacy onboarding progress keys. */
export const ONBOARDING_STEP_LABELS: Record<keyof UserProgress, string> = {
  login: 'Account created',
  welcome: 'Onboarding walkthrough finished',
  details: 'Profile details completed',
  firstPrompt: 'First prompt video recorded',
  allPrompts: 'Four prompt videos recorded',
  profilePic: 'Profile photo added',
};

function legacyFlags(
  user: Record<string, unknown> | null | undefined,
  prompts: unknown[]
): UserProgress {
  const promptVideos = prompts.filter(
    (p) =>
      p &&
      typeof p === 'object' &&
      (p as { fileUrl?: string }).fileUrl &&
      (p as { fileUrl?: string }).fileUrl !== 'N/A'
  ).length;

  return {
    login: true,
    welcome: Boolean(user?.onboardingCompleted ?? user?.onboarding_completed),
    details: Boolean(user?.firstName && user?.country),
    firstPrompt: promptVideos >= 1,
    allPrompts: promptVideos >= 4,
    profilePic: Boolean(user?.profileImg),
  };
}

export function computeUserProgress(
  user: Record<string, unknown> | null | undefined,
  prompts: unknown[] | null | undefined,
  portfolio: Record<string, unknown> | null | undefined
): ProgressResult {
  const list = Array.isArray(prompts) ? prompts : [];
  const result = resolveScreeningReadiness(user, list, portfolio);
  const flags = legacyFlags(user, list);

  const breakdown = (Object.entries(LEGACY_PROGRESS_WEIGHTS) as [keyof UserProgress, number][]).map(
    ([step, weight]) => ({
      step,
      done: flags[step],
      weight,
    })
  );

  return {
    percent: result.percent,
    breakdown,
    sections: result.breakdown,
    suggestions: result.suggestions,
  };
}

/** Short labels for screening readiness UI (matches profile-completion-weights.md). */
export const PROFILE_SECTION_SHORT_LABELS: Record<ProfileCompletionSection, string> = {
  basicProfile: 'Profile basics',
  prompts: 'Screening prompts',
  portfolio: 'Portfolio',
  profilePhoto: 'Profile photo',
  resume: 'Resume',
  languages: 'Languages',
  references: 'References',
  professionalLinks: 'Professional links',
  workCulture: 'Ideal work culture',
};

export function formatSectionWeightLabel(section: ProfileCompletionSection): string {
  return `${PROFILE_SECTION_SHORT_LABELS[section]} (${PROFILE_COMPLETION_WEIGHTS[section]}% of total)`;
}

export type { ProfileCompletionBreakdownItem };

export {
  computeProfileCompletionScore,
  profileCompletionMessage,
  PROFILE_COMPLETION_SECTION_LABELS,
  TARGET_SCREENING_PROMPT_COUNT,
  screeningPromptProgress,
  countScreeningPrompts,
  type ProfileCompletionSection,
};
